import { useCallback, useEffect, useRef, useState } from "react";
import { AnimationState } from "../components/AnimatedDropdown/AnimatedDropdown";

/**
 * Used for elements where content needs to be shown on click of the original
 * element, and hidden when the user clicks anywhere in viewport outside of the
 * original element and its content.
 *
 * E.g. filter buttons, new navbar's pfp to show profile actions.
 */

interface Options {
  duration?: number;
  onExit?: () => void;
}

export const useDisplayOnClick = <T extends HTMLElement>(options?: Options) => {
  const elementToClickRef = useRef<T | undefined>(undefined);
  const elementToDisplayRef = useRef<T | undefined>(undefined);
  const [isDisplayed, setIsDisplayed] = useState(false);
  const [animationState, setAnimationState] = useState<AnimationState>(AnimationState.None);

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!isDisplayed) return;

      const elementToClickBounds = elementToClickRef.current?.getBoundingClientRect();
      const elementToDisplayBounds = elementToDisplayRef.current?.getBoundingClientRect();

      if (elementToClickBounds == null || elementToDisplayBounds == null) {
        return;
      }

      const isClickInElementToClick = Boolean(
        elementToClickBounds &&
          e.clientX >= elementToClickBounds.left &&
          e.clientX <= elementToClickBounds.right &&
          e.clientY >= elementToClickBounds.top &&
          e.clientY <= elementToClickBounds.bottom
      );

      const isClickInElementToDisplay = Boolean(
        elementToDisplayBounds &&
          elementToDisplayBounds.x !== 0 &&
          e.clientX >= elementToDisplayBounds.left &&
          e.clientX <= elementToDisplayBounds.right &&
          e.clientY >= elementToDisplayBounds.top &&
          e.clientY <= elementToDisplayBounds.bottom
      );

      if (isClickInElementToClick || isClickInElementToDisplay) {
        return;
      }

      if (
        isClickInElementToDisplay ||
        // Fix for if mousedown is triggered >1, where top/bottom/left/right is reset to 0
        (elementToDisplayBounds && elementToDisplayBounds.top === 0)
      ) {
        return;
      }

      if (options?.onExit != null) {
        options.onExit();
      }
      setAnimationState(AnimationState.Closing);
      if (options?.duration) {
        setTimeout(() => setIsDisplayed(false), options.duration);
      } else {
        setIsDisplayed(false);
      }
    },
    [isDisplayed, options]
  );

  const onClick = useCallback(() => {
    const animationState = isDisplayed ? AnimationState.Closing : AnimationState.Opening;
    setAnimationState(animationState);

    const toggleDisplay = () => setIsDisplayed(prev => !prev);

    if (isDisplayed && options?.duration) {
      setTimeout(toggleDisplay, options.duration);
    } else {
      toggleDisplay();
    }
  }, [isDisplayed, options?.duration]);

  const forceClose = useCallback(() => {
    setIsDisplayed(false);
    setAnimationState(AnimationState.Closing);
  }, []);
  const forceOpen = useCallback(() => {
    setIsDisplayed(true);
    setAnimationState(AnimationState.Opening);
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  });

  return {
    isDisplayed,
    animationState,
    onClick,
    elementToClickRef,
    elementToDisplayRef,
    forceClose,
    forceOpen,
  };
};
